import { create } from "zustand";
import { persist } from "zustand/middleware";
import { toast } from "react-hot-toast";
import { authApi, handleApiError, isApiError } from "@/services/api";
import { SUCCESS_MESSAGES, ERROR_MESSAGES } from "@/data/constants";
import { extractTokenData } from "@/utils/jwtUtils";
import { convertKeysToCamelCase } from "@/utils/caseConverter";
import type {
  User,
  Business,
  LoginCredentials,
  RegisterData,
  AuthResponse,
  RBACTeam,
  RBACRole,
  UserTeamMembership,
} from "@/types";
import type { OrganizationRole } from "@/constants/permissions";

interface RefreshTokenResponse {
  success: boolean;
  message: string;
  data: {
    access_token: string;
    refresh_token: string;
    expires_in: number;
  };
}

interface AuthState {
  user: User | null;
  business: Business | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  token: string | null;
  refreshToken: string | null;

  // RBAC State
  currentTeam: RBACTeam | null;
  currentTeamId: string | null;
  teams: UserTeamMembership[];
  permissions: Record<string, boolean>;
  roles: RBACRole[];
  organizationRole: OrganizationRole | null;
  isOnboarded: boolean;
  teamsLoading: boolean;
  permissionsLoading: boolean;
  teamsError: string | null;
  permissionsError: string | null;

  // Hydration State - tracks if persisted state has been restored
  _hasHydrated: boolean;
}

interface AuthActions {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  updateUser: (user: User) => void;
  updateBusiness: (business: Business) => void;
  refreshAuth: () => Promise<void>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setAuth: (
    user: User,
    business: Business,
    token: string,
    refreshToken: string
  ) => void;
  clearAuth: () => void;

  // RBAC Actions
  setCurrentTeam: (team: RBACTeam) => void;
  setCurrentTeamId: (teamId: string) => void;
  clearTeamContext: () => void;
  setTeams: (teams: UserTeamMembership[]) => void;
  setPermissions: (permissions: Record<string, boolean>) => void;
  setRoles: (roles: RBACRole[]) => void;
  setOrganizationRole: (role: OrganizationRole) => void;
  setIsOnboarded: (isOnboarded: boolean) => void;
  hasPermission: (permissionKey: string) => boolean;
  hasAnyPermission: (permissionKeys: string[]) => boolean;
  hasAllPermissions: (permissionKeys: string[]) => boolean;
  hasRole: (roleName: string) => boolean;
  setTeamsLoading: (loading: boolean) => void;
  setPermissionsLoading: (loading: boolean) => void;
  setTeamsError: (error: string | null) => void;
  setPermissionsError: (error: string | null) => void;

  // Team fetching actions
  fetchTeams: () => Promise<UserTeamMembership[]>;
  fetchPermissions: (teamId: string) => Promise<Record<string, boolean>>;
  setTeamContext: (teamId: string) => Promise<void>;
  validateTeamContext: () => Promise<boolean>;

  // Hydration actions
  setHasHydrated: (hasHydrated: boolean) => void;
}

type AuthStore = AuthState & AuthActions;

const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      business: null,
      loading: false,
      error: null,
      isAuthenticated: false,
      token: null,
      refreshToken: null,

      // RBAC Initial state
      currentTeam: null,
      currentTeamId: null,
      teams: [],
      permissions: {},
      roles: [],
      organizationRole: null,
      isOnboarded: false,
      teamsLoading: false,
      permissionsLoading: false,
      teamsError: null,
      permissionsError: null,

      // Hydration state - false until persist middleware restores state
      _hasHydrated: false,

      // Actions
      setLoading: (loading) => set({ loading }),

      setError: (error) => set({ error }),

      clearError: () => set({ error: null }),

      setAuth: (user, business, token, refreshToken) => {
        // Extract RBAC data from token
        const tokenData = extractTokenData(token);

        set({
          user,
          business,
          token,
          refreshToken,
          isAuthenticated: true,
          loading: false,
          error: null,
          // Set RBAC data from token
          roles: tokenData?.roles || [],
          permissions: tokenData?.permissions || {},
          isOnboarded: tokenData?.isOnboarded ?? false,
        });
      },

      clearAuth: () => {
        set({
          user: null,
          business: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          loading: false,
          error: null,
          // Clear RBAC state
          currentTeam: null,
          currentTeamId: null,
          teams: [],
          permissions: {},
          roles: [],
          organizationRole: null,
          isOnboarded: false,
          teamsLoading: false,
          permissionsLoading: false,
          teamsError: null,
          permissionsError: null,
        });
      },

      updateUser: (user) => set({ user }),

      updateBusiness: (business) => set({ business }),

      login: async (credentials) => {
        try {
          set({ loading: true, error: null });

          const response = await authApi.login(credentials);
          const loginData = response.data.data as AuthResponse;
          const { user: rawUser, business: rawBusiness, access_token, refresh_token, team_memberships, organization } = loginData;

          // Convert snake_case API response to camelCase for frontend
          const user = convertKeysToCamelCase(rawUser) as User;

          // Handle case where business might not be in response (use organization instead)
          let business: Business;
          if (rawBusiness) {
            business = convertKeysToCamelCase(rawBusiness) as Business;
          } else if (organization) {
            // Fallback: create business object from organization
            business = {
              id: organization.id,
              name: organization.name,
              is_onboarded: organization.is_onboarded,
              status: 'active',
              subscription_plan: '',
              settings: {},
              deleted: false,
            } as Business;
          } else {
            throw new Error('No business or organization data in login response');
          }

          // Set is_onboarded from organization if available (override if organization has it)
          if (organization && typeof organization.is_onboarded === 'boolean') {
            business.is_onboarded = organization.is_onboarded;
          }

          // Set basic auth data
          get().setAuth(user, business, access_token, refresh_token);

          // Convert and store team memberships from login response
          if (team_memberships && team_memberships.length > 0) {
            const teams: UserTeamMembership[] = team_memberships.map((tm) => ({
              team: {
                id: tm.team_id,
                name: tm.team_name,
                status: 'active' as const,
                business_id: user.businessId || organization?.id || '',
                created_by: '',
                created_at: tm.joined_at,
              },
              role: {
                id: tm.role_name,
                name: tm.role_name,
                description: tm.role_name,
              },
              joined_at: tm.joined_at,
            }));
            set({ teams });
          }

          // Set organization role from user
          if (user.roles && user.roles.length > 0) {
            const orgRole = user.roles[0]?.name as OrganizationRole;
            if (orgRole) {
              set({ organizationRole: orgRole });
            }
          }

          toast.success(SUCCESS_MESSAGES.LOGIN_SUCCESS);
        } catch (error) {
          const errorMessage = isApiError(error)
            ? handleApiError(error)
            : ERROR_MESSAGES.UNKNOWN_ERROR;
          set({ error: errorMessage, loading: false });
          throw error;
        }
      },

      register: async (userData) => {
        try {
          set({ loading: true, error: null });

          const response = await authApi.register(userData);
          const { user: rawUser, business: rawBusiness, access_token, refresh_token, organization } = response.data
            .data as AuthResponse;

          // Convert snake_case API response to camelCase for frontend
          const user = convertKeysToCamelCase(rawUser) as User;

          // Handle case where business might not be in response (use organization instead)
          let business: Business;
          if (rawBusiness) {
            business = convertKeysToCamelCase(rawBusiness) as Business;
          } else if (organization) {
            // Fallback: create business object from organization
            business = {
              id: organization.id,
              name: organization.name,
              is_onboarded: organization.is_onboarded,
              status: 'active',
              subscription_plan: '',
              settings: {},
              deleted: false,
            } as Business;
          } else {
            throw new Error('No business or organization data in register response');
          }

          // Set is_onboarded from organization if available
          if (organization && typeof organization.is_onboarded === 'boolean') {
            business.is_onboarded = organization.is_onboarded;
          }

          get().setAuth(user, business, access_token, refresh_token);
          toast.success(SUCCESS_MESSAGES.REGISTER_SUCCESS);
        } catch (error) {
          const errorMessage = isApiError(error)
            ? handleApiError(error)
            : ERROR_MESSAGES.UNKNOWN_ERROR;
          set({ error: errorMessage, loading: false });
          throw error;
        }
      },

      logout: async () => {
        try {
          const { token } = get();
          if (token) {
            await authApi.logout();
          }
        } catch (error) {
          console.error("Logout error:", error);
        } finally {
          // Disconnect conversation socket before clearing auth
          try {
            const { useConversationStore } = await import('@/stores/conversation');
            const { disconnectSocket } = useConversationStore.getState();
            disconnectSocket();
          } catch (error) {
            console.error("Error disconnecting conversation socket:", error);
          }
          
          get().clearAuth();
          toast.success(SUCCESS_MESSAGES.LOGOUT_SUCCESS);
          // Navigate to login page
          window.location.href = "/";
        }
      },

      refreshAuth: async () => {
        try {
          const { refreshToken } = get();

          if (!refreshToken) {
            throw new Error("No refresh token available");
          }

          const response = await authApi.refresh(refreshToken);

          if (response.data.success) {
            const refreshData = response.data
              .data as RefreshTokenResponse["data"];
            const { access_token, refresh_token: newRefreshToken } =
              refreshData;

            // Extract RBAC data from new token
            const tokenData = extractTokenData(access_token);

            // Update tokens and RBAC data in store
            // Note: Team-specific permissions come from fetchPermissions() API,
            // not from the JWT. The JWT may contain an empty permissions object,
            // so we only overwrite if the token actually has permission data.
            const hasTokenPermissions = tokenData?.permissions && Object.keys(tokenData.permissions).length > 0;
            const hasTokenRoles = tokenData?.roles && tokenData.roles.length > 0;

            set((state) => ({
              ...state,
              token: access_token,
              refreshToken: newRefreshToken,
              // Preserve existing RBAC data unless the refreshed token has actual data
              roles: hasTokenRoles ? tokenData.roles : state.roles,
              permissions: hasTokenPermissions ? tokenData.permissions : state.permissions,
              isOnboarded: tokenData?.isOnboarded ?? state.isOnboarded,
            }));
          } else {
            throw new Error(response.data.message || "Token refresh failed");
          }
        } catch (error) {
          console.error("Token refresh failed:", error);
          get().clearAuth();
          throw error;
        }
      },

      // ========================================
      // RBAC Actions
      // ========================================

      setCurrentTeam: (team) =>
        set({
          currentTeam: team,
          currentTeamId: team.id,
        }),

      setCurrentTeamId: (teamId) =>
        set({ currentTeamId: teamId }),

      clearTeamContext: () =>
        set({
          currentTeam: null,
          currentTeamId: null,
          permissions: {},
          roles: [],
        }),

      setTeams: (teams) =>
        set({ teams }),

      setPermissions: (permissions) =>
        set({ permissions }),

      setRoles: (roles) =>
        set({ roles }),

      setOrganizationRole: (role) =>
        set({ organizationRole: role }),

      setIsOnboarded: (isOnboarded) =>
        set({ isOnboarded }),

      setTeamsLoading: (teamsLoading) =>
        set({ teamsLoading }),

      setPermissionsLoading: (permissionsLoading) =>
        set({ permissionsLoading }),

      setTeamsError: (teamsError) =>
        set({ teamsError }),

      setPermissionsError: (permissionsError) =>
        set({ permissionsError }),

      // Hydration action - called by onRehydrateStorage
      setHasHydrated: (hasHydrated) =>
        set({ _hasHydrated: hasHydrated }),

      hasPermission: (permissionKey) => {
        const { permissions } = get();
        // If user has ALL permissions (owner/admin), always return true
        if (permissions._hasAllPermissions) return true;

        // Handle nested permission structure from backend
        // e.g., "channels.view" -> permissions.channels.view
        const parts = permissionKey.split('.');
        if (parts.length === 2) {
          const [category, action] = parts;
          const categoryPermissions = permissions[category];
          if (typeof categoryPermissions === 'object' && categoryPermissions !== null) {
            return categoryPermissions[action] === true;
          }
        }

        // Fallback to flat key lookup for backwards compatibility
        return permissions[permissionKey] === true;
      },

      hasAnyPermission: (permissionKeys) => {
        const { permissions } = get();
        // If user has ALL permissions (owner/admin), always return true
        if (permissions._hasAllPermissions) return true;

        return permissionKeys.some(key => {
          // Handle nested permission structure
          const parts = key.split('.');
          if (parts.length === 2) {
            const [category, action] = parts;
            const categoryPermissions = permissions[category];
            if (typeof categoryPermissions === 'object' && categoryPermissions !== null) {
              return categoryPermissions[action] === true;
            }
          }
          return permissions[key] === true;
        });
      },

      hasAllPermissions: (permissionKeys) => {
        const { permissions } = get();
        // If user has ALL permissions (owner/admin), always return true
        if (permissions._hasAllPermissions) return true;

        return permissionKeys.every(key => {
          // Handle nested permission structure
          const parts = key.split('.');
          if (parts.length === 2) {
            const [category, action] = parts;
            const categoryPermissions = permissions[category];
            if (typeof categoryPermissions === 'object' && categoryPermissions !== null) {
              return categoryPermissions[action] === true;
            }
          }
          return permissions[key] === true;
        });
      },

      hasRole: (roleName) => {
        const { roles } = get();
        return roles.some(
          role => role.name.toLowerCase() === roleName.toLowerCase()
        );
      },

      // ========================================
      // Team Fetching Actions
      // ========================================

      fetchTeams: async () => {
        try {
          set({ teamsLoading: true, teamsError: null });
          const response = await authApi.getTeams();

          // API may return flat structure or nested structure
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const teamsResponse = response.data as any;
          const rawTeams = teamsResponse.data || [];

          // Transform flat API response to UserTeamMembership format if needed
          const teams: UserTeamMembership[] = rawTeams.map((item: any) => {
            // Check if already in UserTeamMembership format (has nested team object)
            if (item.team && typeof item.team === 'object' && item.team.id) {
              return item as UserTeamMembership;
            }

            // Transform flat structure (team_id, team_name, role_name) to nested format
            return {
              team: {
                id: item.team_id || item.id,
                name: item.team_name || item.name,
                status: item.status || 'active',
                business_id: item.business_id || '',
                created_by: item.created_by || '',
                created_at: item.created_at || item.joined_at || '',
              },
              role: {
                id: item.role?.id || item.role_id || item.role_name || '',
                name: item.role?.name || item.role_name || '',
                description: item.role?.description || item.role_name || '',
              },
              joined_at: item.joined_at || '',
            } as UserTeamMembership;
          });

          set({ teams, teamsLoading: false });
          return teams;
        } catch (error) {
          const errorMessage = isApiError(error)
            ? handleApiError(error)
            : 'Failed to fetch teams';
          set({ teamsError: errorMessage, teamsLoading: false });
          throw error;
        }
      },

      fetchPermissions: async (teamId) => {
        try {
          set({ permissionsLoading: true, permissionsError: null });
          const response = await authApi.getPermissions(teamId);
          // API returns: { success: true, data: { team_id, role, permissions: "ALL" | Record<string, boolean> } }
          const apiResponse = response.data as unknown as {
            success: boolean;
            data: {
              team_id: string;
              role: { name: string; display_name: string };
              permissions: string | Record<string, boolean>;
            }
          };

          let permissions: Record<string, boolean> = {};

          // Handle "ALL" permissions (owner/admin with full access)
          if (apiResponse.data?.permissions === 'ALL') {
            // Set a special flag for full permissions
            permissions = { _hasAllPermissions: true };
          } else if (typeof apiResponse.data?.permissions === 'object') {
            permissions = apiResponse.data.permissions;
          }

          set({ permissions, permissionsLoading: false });
          return permissions;
        } catch (error) {
          const errorMessage = isApiError(error)
            ? handleApiError(error)
            : 'Failed to fetch permissions';
          set({ permissionsError: errorMessage, permissionsLoading: false });
          throw error;
        }
      },

      setTeamContext: async (teamId) => {
        try {
          set({ teamsLoading: true });

          // Call API to set team context
          await authApi.setTeamContext({ team_id: teamId });

          // Find the team from the teams array
          const { teams } = get();
          const selectedTeam = teams.find(t => t.team.id === teamId);

          if (selectedTeam) {
            set({
              currentTeam: selectedTeam.team,
              currentTeamId: teamId,
              roles: [selectedTeam.role],
              teamsLoading: false,
            });

            // Fetch permissions for this team
            await get().fetchPermissions(teamId);
          } else {
            set({ currentTeamId: teamId, teamsLoading: false });
          }
        } catch (error) {
          set({ teamsLoading: false });
          throw error;
        }
      },

      validateTeamContext: async () => {
        try {
          const { currentTeamId } = get();
          if (!currentTeamId) return false;

          const response = await authApi.validateTeamContext(currentTeamId);
          // API returns: { success: true, data: { valid: true, role_name: "owner" } }
          const apiResponse = response.data as unknown as {
            success: boolean;
            data: { valid: boolean; role_name?: string }
          };
          const isValid = apiResponse.data?.valid ?? false;

          if (!isValid) {
            // Clear invalid team context
            get().clearTeamContext();
          }

          return isValid;
        } catch (error) {
          console.error('Team context validation failed:', error);
          get().clearTeamContext();
          return false;
        }
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        user: state.user,
        business: state.business,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        // RBAC persisted state
        currentTeam: state.currentTeam,
        currentTeamId: state.currentTeamId,
        teams: state.teams,
        permissions: state.permissions,
        roles: state.roles,
        organizationRole: state.organizationRole,
        isOnboarded: state.isOnboarded,
      }),
      // Called when hydration is complete - sets _hasHydrated to true
      // This prevents race conditions during HMR where components read
      // initial (empty) state before localStorage data is restored
      onRehydrateStorage: () => (state) => {
        // When hydration completes, mark the store as hydrated
        // This happens after persisted state is restored from localStorage
        if (state) {
          state.setHasHydrated(true);
        }
      },
    }
  )
);

export default useAuthStore;

// Convenience hooks for specific auth state
export const useAuth = () => useAuthStore();
export const useUser = () => useAuthStore((state) => state.user);
export const useBusiness = () => useAuthStore((state) => state.business);
export const useIsAuthenticated = () =>
  useAuthStore((state) => state.isAuthenticated);
export const useAuthLoading = () => useAuthStore((state) => state.loading);
export const useAuthError = () => useAuthStore((state) => state.error);

// RBAC convenience hooks
export const useCurrentTeam = () => useAuthStore((state) => state.currentTeam);
export const useCurrentTeamId = () => useAuthStore((state) => state.currentTeamId);
export const useTeams = () => useAuthStore((state) => state.teams);
export const usePermissions = () => useAuthStore((state) => state.permissions);
export const useRoles = () => useAuthStore((state) => state.roles);
export const useOrganizationRole = () => useAuthStore((state) => state.organizationRole);
export const useIsOnboarded = () => useAuthStore((state) => state.isOnboarded);
export const useTeamsLoading = () => useAuthStore((state) => state.teamsLoading);
export const usePermissionsLoading = () => useAuthStore((state) => state.permissionsLoading);

// RBAC action hooks
export const useHasPermission = () => useAuthStore((state) => state.hasPermission);
export const useHasAnyPermission = () => useAuthStore((state) => state.hasAnyPermission);
export const useHasAllPermissions = () => useAuthStore((state) => state.hasAllPermissions);
export const useHasRole = () => useAuthStore((state) => state.hasRole);

// Team action hooks
export const useFetchTeams = () => useAuthStore((state) => state.fetchTeams);
export const useFetchPermissions = () => useAuthStore((state) => state.fetchPermissions);
export const useSetTeamContext = () => useAuthStore((state) => state.setTeamContext);
export const useValidateTeamContext = () => useAuthStore((state) => state.validateTeamContext);
export const useClearTeamContext = () => useAuthStore((state) => state.clearTeamContext);

// Hydration hook - use to wait for persisted state to be restored
// Critical for preventing race conditions during HMR updates
export const useHasHydrated = () => useAuthStore((state) => state._hasHydrated);
